import { useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import ThemeToggle from "@/components/ThemeToggle";
import { motion } from "framer-motion";
import { Rocket, ArrowRight, Briefcase, Bot, Users, Zap, Trophy, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import Footer from "@/components/Footer";
import MeetFounder from "@/components/MeetFounder";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function loginRedirect() {
  const redirectUrl = window.location.origin + "/hq";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

const FEATURES = [
  { icon: Briefcase, title: "You get hired, not enrolled", body: "Skip the dashboard. Receive a real job offer and start your first project in 15 minutes." },
  { icon: Bot, title: "An AI senior PM mentors you", body: "Pause any moment and ask 'what would an experienced PM do here?' Your mentor teaches, not tells." },
  { icon: Users, title: "Stakeholders with attitudes", body: "Negotiate with impatient sponsors, cautious inspectors, and aggressive sales leads — each with their own agenda." },
  { icon: Zap, title: "No two projects are the same", body: "Budget cuts, permit delays, cyber incidents and scope creep hit dynamically. Adapt or the metrics slip." },
  { icon: FileText, title: "Graded on real deliverables", body: "Write status reports, risk briefings and executive updates. AI grades every word against a rubric." },
  { icon: Trophy, title: "Build a career, earn the title", body: "Climb from Project Assistant to Chief Project Officer. Every win generates portfolio evidence employers trust." },
];

const INDUSTRIES = ["Information Technology", "Construction", "Healthcare", "Manufacturing", "Supply Chain & Logistics"];

export default function Landing() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  useEffect(() => { if (!loading && user) navigate("/hq"); }, [user, loading, navigate]);

  return (
    <div className="min-h-screen bg-obsidian text-white relative z-10 overflow-hidden">
      <div className="absolute inset-0 z-0 opacity-40"
        style={{ backgroundImage: "url(https://images.unsplash.com/photo-1700316740839-f5afe22536e4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHw0fHxwcmVtaXVtJTIwZGFyayUyMHRlY2glMjBnZW9tZXRyaWMlMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc4MzkwMzAwNXww&ixlib=rb-4.1.0&q=85)", backgroundSize: "cover", backgroundPosition: "center" }} />
      <div className="absolute inset-0 z-0 bg-gradient-to-b from-obsidian/70 via-obsidian/90 to-obsidian" />

      <header className="relative z-20 max-w-[1400px] mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <span className="w-8 h-8 grid place-items-center rounded-md bg-volt"><Rocket className="w-4 h-4" /></span>
          <span className="font-heading font-extrabold tracking-tight text-lg">PM LaunchPad<span className="text-volt">.AI</span></span>
        </div>
        <div className="flex items-center gap-3">
          <ThemeToggle />
          <Link to="/pricing" className="text-sm text-white/70 hover:text-white transition-colors duration-200 hidden sm:block">Pricing</Link>
          <Button data-testid="header-login-btn" onClick={loginRedirect} className="rounded-full bg-white text-obsidian hover:bg-white/90">Sign in</Button>
        </div>
      </header>

      <section className="relative z-20 max-w-[1400px] mx-auto px-4 md:px-8 pt-16 md:pt-28 pb-24">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-3xl">
          <span className="overline text-volt">The apprenticeship for project managers</span>
          <h1 className="font-heading font-extrabold tracking-tighter text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[1.05]">
            Get real project experience. <span className="text-volt">Before</span> you get the job.
          </h1>
          <p className="text-base md:text-lg text-white/70 mt-6 leading-relaxed max-w-2xl">
            PM LaunchPad drops you into a simulated career. You'll manage real projects across five industries,
            navigate demanding stakeholders, and finish able to say: <em className="text-white">"I've already managed 25 projects."</em>
          </p>
          <div className="flex flex-wrap items-center gap-4 mt-9">
            <Button data-testid="hero-start-btn" onClick={loginRedirect} size="lg"
              className="rounded-full bg-volt hover:bg-volt/90 text-white text-base h-12 px-7">
              Accept your job offer <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
            <span className="text-sm text-white/50 font-mono">Free tier · 3 simulations · no card</span>
          </div>
          <div className="flex flex-wrap gap-2 mt-10">
            {INDUSTRIES.map((i) => (
              <span key={i} className="text-xs font-mono px-3 py-1.5 rounded-full border border-white/15 text-white/70">{i}</span>
            ))}
          </div>
        </motion.div>
      </section>

      <section className="relative z-20 max-w-[1400px] mx-auto px-4 md:px-8 pb-28">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-l border-white/10">
          {FEATURES.map((f, i) => (
            <motion.div key={f.title} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.05 }}
              className="p-8 border-r border-b border-white/10 hover:bg-white/[0.03] transition-colors duration-200">
              <f.icon className="w-6 h-6 text-volt" />
              <h3 className="font-heading font-bold text-xl mt-5">{f.title}</h3>
              <p className="text-white/60 mt-2 leading-relaxed text-sm">{f.body}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <MeetFounder />

      <section className="relative z-20 max-w-[1400px] mx-auto px-4 md:px-8 pb-28">
        <div className="rounded-lg border border-white/10 bg-white/[0.03] p-10 md:p-16 text-center">
          <h2 className="font-heading font-extrabold tracking-tight text-2xl sm:text-3xl lg:text-4xl">Your first day starts now.</h2>
          <p className="text-white/60 mt-4 max-w-xl mx-auto">Sign in with Google and meet your manager, your mentor, and your first project.</p>
          <Button data-testid="cta-login-btn" onClick={loginRedirect} size="lg" className="rounded-full bg-volt hover:bg-volt/90 text-white mt-8 h-12 px-8">
            Start free <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
        <p className="text-center text-white/30 text-xs mt-16 font-mono">PM LaunchPad AI · Duolingo × Flight Simulator × Jira for project management</p>
      </section>

      <Footer variant="marketing" />
    </div>
  );
}
